import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { GalleryItemDto, UserDirectoryItem } from '@crm/shared';
import { GALLERY_LIMIT_PER_USER } from '@crm/shared';
import { extractApiError } from '../../lib/api';
import { assetUrl } from '../../lib/assetUrl';
import { useAuth } from '../auth/AuthContext';
import { useUserDirectory } from '../schedules/api';
import { useServicesList } from '../services/api';
import {
  useGalleryDelete,
  useGalleryList,
} from './api';
import { GalleryUploadModal } from './GalleryUploadModal';
import { GalleryLightbox } from './GalleryLightbox';

export function PortfolioPage(): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  const directoryQuery = useUserDirectory();
  const servicesQuery = useServicesList({ active: 'true' });

  /**
   * Admin może wybrać dowolnego bookable pracownika. Pracownik widzi
   * tylko siebie — selector ukryty. RECEPTION nie ma tej strony (RBAC
   * w route).
   */
  const bookableEmployees: UserDirectoryItem[] = useMemo(
    () => (directoryQuery.data ?? []).filter((u) => u.bookable && u.active),
    [directoryQuery.data],
  );

  // Inicjalny wybór: pracownik → własne; admin → pierwszy bookable.
  const [selectedUserId, setSelectedUserId] = useState<number | null>(() => {
    if (!user) return null;
    if (!isAdmin) return user.id;
    return null;
  });

  // Po załadowaniu directory: admin domyślnie wybiera pierwszego bookable.
  // useEffect, żeby uniknąć setState-in-render anti-patternu.
  useEffect(() => {
    if (isAdmin && selectedUserId === null && bookableEmployees.length > 0) {
      setSelectedUserId(bookableEmployees[0]!.id);
    }
  }, [isAdmin, selectedUserId, bookableEmployees]);

  const galleryQuery = useGalleryList({
    userId: selectedUserId ?? undefined,
  });
  const items: GalleryItemDto[] = useMemo(() => {
    return (galleryQuery.data ?? []).filter((it) => it.userId === selectedUserId);
  }, [galleryQuery.data, selectedUserId]);

  const del = useGalleryDelete();
  const [actionError, setActionError] = useState<string | null>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const onDelete = async (item: GalleryItemDto) => {
    setActionError(null);
    if (!window.confirm(t('portfolio.confirmDelete'))) return;
    try {
      await del.mutateAsync(item.id);
    } catch (err) {
      setActionError(extractApiError(err));
    }
  };

  const selectedEmployee = bookableEmployees.find((u) => u.id === selectedUserId) ?? null;
  const canManage = isAdmin || (user?.id === selectedUserId);
  const atLimit = items.length >= GALLERY_LIMIT_PER_USER;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">{t('portfolio.title')}</h1>
          <p className="mt-1 text-sm text-zinc-500">{t('portfolio.subtitle')}</p>
        </div>
        {canManage && selectedUserId !== null && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setUploadOpen(true)}
            disabled={atLimit}
            title={atLimit ? t('portfolio.limitReached', { limit: GALLERY_LIMIT_PER_USER }) : undefined}
          >
            {t('portfolio.addPhoto')}
          </button>
        )}
      </header>

      {isAdmin && (
        <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-zinc-200">
          <label className="block text-sm font-medium text-zinc-700">
            {t('portfolio.employee')}
          </label>
          <select
            value={selectedUserId ?? ''}
            onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
            className="input mt-2 max-w-md"
          >
            <option value="">{t('portfolio.selectEmployee')}</option>
            {bookableEmployees.map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </select>
        </div>
      )}

      {actionError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {actionError}
        </div>
      )}

      {atLimit && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          {t('portfolio.limitReached', { limit: GALLERY_LIMIT_PER_USER })}
        </div>
      )}

      {galleryQuery.isLoading ? (
        <div className="text-sm text-zinc-500">{t('common.loading')}</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-12 text-center text-sm text-zinc-500">
          {selectedEmployee
            ? t('portfolio.empty', { name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}` })
            : t('portfolio.emptyNoSelection')}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((item, idx) => (
            <article
              key={item.id}
              className="group overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-zinc-200 transition hover:shadow"
            >
              <button
                type="button"
                onClick={() => setLightboxIndex(idx)}
                className="block w-full overflow-hidden bg-zinc-100"
                aria-label={item.caption ?? t('portfolio.photo')}
              >
                <img
                  src={assetUrl(item.thumbnailUrl ?? item.imageUrl)}
                  alt={item.caption ?? ''}
                  loading="lazy"
                  className="aspect-square w-full object-cover transition group-hover:scale-105"
                />
              </button>
              <div className="space-y-2 p-3">
                {item.caption && (
                  <div className="line-clamp-2 text-sm text-zinc-800">{item.caption}</div>
                )}
                {item.service && (
                  <span className="inline-block rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700">
                    {item.service.name}
                  </span>
                )}
                {canManage && (
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => onDelete(item)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      {t('common.actions.delete')}
                    </button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {uploadOpen && selectedUserId !== null && (
        <GalleryUploadModal
          userId={selectedUserId}
          services={servicesQuery.data ?? []}
          onClose={() => setUploadOpen(false)}
        />
      )}

      {lightboxIndex !== null && items.length > 0 && (
        <GalleryLightbox
          items={items}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
